/**
 * TranslationReview — the opened family's translation approval (lot D step 10, UI-SPEC §2.17).
 *
 * Source text pinned above; one group per language (header checkbox · flag ·
 * name · « {k} sur {n} »); rows with the tracked proper nouns highlighted, an
 * amber gap per missing one, a chip per flag. Foot: « Retour » · « Approuver {ok} sur {all} ».
 *
 * ONE `preview: true` call when the review opens and after each change of the
 * approved set (items = the proposals' text + token, unchanged). [B] `refused`
 * greys « Approuver » with its LimitReasonLine; [A] non-empty `warnings` open
 * QuotaWarningDialog first. The save sends the same body without `preview`:
 * created languages leave, refused ones stay with their line, a 409
 * ALLOWANCE_EXCEEDED keeps every row. No fit is computed here.
 */
import { useEffect, useRef, useState } from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type {
  BrandQuery,
  BrandQueryAxis,
  CreateLanguageVariantsResponse,
  ProperNounFlag,
  TranslationProposal,
} from '@traaker/shared';
import { brandQueriesApi } from '../../services/brand-queries';
import type { LimitSlot, PromptAllowanceFigures } from './promptStudioModel';
import { firstRunLines, leftLines, limitReason, slotText, structuralRowError } from './promptStudioModel';
import { languageName } from './variantModel';
import { LimitReasonLine } from './LimitReasonLine';
import { QuotaWarningDialog } from './QuotaWarningDialog';
import { Flag } from '../../pages/dashboard/Flag';
import { languageFlag } from '../../pages/dashboard/geoFlags';

// ---------------------------------------------------------------------------
// Pure decisions (tested in TranslationReview.test.tsx)
// ---------------------------------------------------------------------------

export type ApproveItem = { language: string; text: string; token: string };
export type Refusal = { reason: LimitSlot | null; left: LimitSlot[] };
export type PreviewState = { status: 'idle' | 'loading' | 'done' | 'failed'; res?: CreateLanguageVariantsResponse };

/** The languages checked when the review opens: `defaultChecked` approvable proposals. */
export function initialSelection(proposals: TranslationProposal[]): Set<string> {
  return new Set(proposals.filter((p) => p.defaultChecked && approvable(p)).map((p) => p.language));
}

const approvable = (p: TranslationProposal) => p.approvable && p.text !== undefined && p.token !== undefined;

/** The approved items: each proposal's text and token UNCHANGED (never trimmed). */
export function approvedItems(proposals: TranslationProposal[], selected: ReadonlySet<string>): ApproveItem[] {
  return proposals
    .filter((p) => selected.has(p.language) && approvable(p))
    .map((p) => ({ language: p.language, text: p.text as string, token: p.token as string }));
}

/** [B] a preview that says `refused`: reason from its figuresAfter, « il reste » from the current figures (lot D and lot E). */
export function previewRefusal(
  res: Pick<CreateLanguageVariantsResponse, 'refused' | 'figuresAfter'>,
  isAgency: boolean,
  current: PromptAllowanceFigures | null,
  axis: BrandQueryAxis,
): Refusal | null {
  if (isAgency || res.refused !== true) return null;
  return { reason: res.figuresAfter ? limitReason(res.figuresAfter, 'promptRequests') : null, left: leftLines(current, [axis]) };
}

/** A save answered 409 ALLOWANCE_EXCEEDED: reason from its figuresAfter, « il reste » from its figures. */
export function saveRefusal(err: unknown, axis: BrandQueryAxis): Refusal | null {
  const e = err as { status?: number; body?: { code?: string; figures?: PromptAllowanceFigures; figuresAfter?: PromptAllowanceFigures } };
  if (e?.status !== 409 || e.body?.code !== 'ALLOWANCE_EXCEEDED') return null;
  return {
    reason: e.body.figuresAfter ? limitReason(e.body.figuresAfter, 'promptRequests') : null,
    left: leftLines(e.body.figures ?? null, [axis]),
  };
}

/** The save's per-item errors, one code per language (its first). */
export function rowErrors(res: CreateLanguageVariantsResponse): Record<string, string> {
  const out: Record<string, string> = {};
  for (const e of res.errors ?? []) if (!(e.language in out)) out[e.language] = e.error;
  return out;
}

/** The line under a refused row: the structural limits, else the generic save failure. */
export function rowErrorSlot(code: string, axis: BrandQueryAxis): LimitSlot {
  return structuralRowError(code, axis, 'save') ?? { key: 'promptStudio.common.saveFailed', values: {} };
}

/** « Approuver » state. Disabled while nothing is approved, saving, the preview is not done, or refused. */
export function approveFoot(a: {
  ok: number;
  saving: boolean;
  preview: PreviewState;
  isAgency: boolean;
  current: PromptAllowanceFigures | null;
  axis: BrandQueryAxis;
  refused409: Refusal | null;
}): { disabled: boolean; refusal: Refusal | null; previewFailed: boolean } {
  const fromPreview = a.preview.status === 'done' && a.preview.res
    ? previewRefusal(a.preview.res, a.isAgency, a.current, a.axis)
    : null;
  const refusal = a.refused409 ?? fromPreview;
  const previewFailed = a.preview.status === 'failed';
  return { disabled: a.ok === 0 || a.saving || a.preview.status !== 'done' || !!refusal, refusal, previewFailed };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface TranslationReviewProps {
  brandId: string;
  root: BrandQuery;
  proposals: TranslationProposal[];
  isAgency: boolean;
  /** The popup's GET …/allowances figures (« il reste » under a refused preview). */
  currentFigures: PromptAllowanceFigures | null;
  onBack: () => void;
  /** A save answered 200: the languages created; `done` = every approved one was (the caller closes the review). */
  onSaved: (res: CreateLanguageVariantsResponse, created: string[], done: boolean) => void;
}

const FLAG_KEY: Record<ProperNounFlag['kind'], string> = {
  missing: 'promptStudio.trans.nounMissing',
  added: 'promptStudio.trans.nounAdded',
  identical: 'promptStudio.trans.sameAsSource',
  place_added: 'promptStudio.trans.placeAdded',
  failed: 'promptStudio.trans.unavailable',
};

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Each `survived` entity highlighted (green `mark.keep`). */
function marked(text: string, survived: string[]): React.ReactNode {
  const labels = survived.filter(Boolean);
  if (labels.length === 0) return text;
  return text.split(new RegExp(`(${labels.map(escapeRe).join('|')})`, 'gi')).map((part, i) =>
    i % 2 ? (
      <Box key={i} component="mark" sx={{ bgcolor: 'rgba(16,185,129,.16)', color: '#047857', fontWeight: 600, borderRadius: 0.5 }}>
        {part}
      </Box>
    ) : (
      part
    ),
  );
}

export function TranslationReview({ brandId, root, proposals, isAgency, currentFigures, onBack, onSaved }: TranslationReviewProps) {
  const { t, i18n } = useTranslation();
  const axis = root.axis as BrandQueryAxis;
  const [list, setList] = useState(proposals);
  const [selected, setSelected] = useState(() => initialSelection(proposals));
  const [preview, setPreview] = useState<PreviewState>({ status: 'idle' });
  const [refused409, setRefused409] = useState<Refusal | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [quotaOpen, setQuotaOpen] = useState(false);
  const seq = useRef(0);

  const items = approvedItems(list, selected);
  const itemsKey = items.map((i) => i.language).join(',');

  // ONE preview on open and after each change of the approved set.
  useEffect(() => {
    const s = ++seq.current;
    setRefused409(null);
    if (items.length === 0) {
      setPreview({ status: 'idle' });
      return;
    }
    setPreview({ status: 'loading' });
    brandQueriesApi
      .createLanguageVariants(brandId, { roots: [{ queryId: root.queryId, axis, items }], preview: true })
      .then(
        (res) => s === seq.current && setPreview({ status: 'done', res }),
        () => s === seq.current && setPreview({ status: 'failed' }),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey]);

  const foot = approveFoot({ ok: items.length, saving, preview, isAgency, current: currentFigures, axis, refused409 });

  const toggle = (langs: string[], on: boolean) =>
    setSelected((s) => {
      const next = new Set(s);
      for (const l of langs) if (on) next.add(l); else next.delete(l);
      return next;
    });

  async function save() {
    setQuotaOpen(false);
    setSaving(true);
    setSaveFailed(false);
    try {
      const res = await brandQueriesApi.createLanguageVariants(brandId, { roots: [{ queryId: root.queryId, axis, items }] });
      const rowErr = rowErrors(res);
      const created = new Set(items.map((i) => i.language).filter((l) => !(l in rowErr)));
      setErrs(rowErr);
      setList((l) => l.filter((p) => !created.has(p.language)));
      setSelected((s) => new Set([...s].filter((l) => !created.has(l))));
      onSaved(res, [...created], Object.keys(rowErr).length === 0);
    } catch (err) {
      const r = saveRefusal(err, axis);
      if (r) setRefused409(r);
      else setSaveFailed(true);
    } finally {
      setSaving(false);
    }
  }

  const onApprove = () => {
    if (foot.disabled) return;
    if (isAgency && preview.res?.warnings?.length) setQuotaOpen(true);
    else void save();
  };

  const languages = [...new Set(list.map((p) => p.language))];
  const firstRun = !isAgency && preview.status === 'done' ? firstRunLines(preview.res?.firstRun ?? null) : [];

  return (
    <Box sx={{ mt: 0.5 }}>
      {/* Source text, pinned */}
      <Box sx={{ fontSize: 13, color: 'text.secondary', bgcolor: 'rgba(0,0,0,.03)', borderRadius: 1.5, px: 1.5, py: 1, mb: 1 }}>
        {root.text}
      </Box>

      {languages.map((lang) => {
        const rows = list.filter((p) => p.language === lang);
        const ok = rows.filter(approvable);
        const k = rows.filter((p) => selected.has(p.language) && approvable(p)).length;
        const name = languageName(lang, i18n.language);
        const flag = languageFlag(lang);
        return (
          <Box key={lang} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, fontSize: 12.5, fontWeight: 600 }}>
              <Box
                component="input"
                type="checkbox"
                checked={ok.length > 0 && k === ok.length}
                disabled={ok.length === 0}
                onChange={() => toggle(ok.map((p) => p.language), k !== ok.length)}
                sx={{ m: 0, accentColor: '#7c3aed' }}
              />
              {flag ? <Flag countryCode={flag} size={15} title={name} /> : lang.toUpperCase()}
              <Box sx={{ flex: 1 }}>{name}</Box>
              <Box component="span" sx={{ fontSize: 11.5, fontWeight: 400, color: 'text.secondary' }}>
                {t('promptStudio.trans.kept', { k, n: rows.length })}
              </Box>
            </Box>

            {rows.map((p) => {
              const flagged = p.flags.length > 0;
              const missing = p.flags.filter((f) => f.kind === 'missing');
              const anyway = p.approvable && p.flags.some((f) => f.kind === 'missing' || f.kind === 'added');
              const err = errs[p.language];
              return (
                <Box key={`${p.rootId}|${p.language}`} sx={{ display: 'flex', gap: 1, px: 1, py: 0.75, borderRadius: 1, bgcolor: flagged ? '#fffdf7' : 'transparent' }}>
                  <Box
                    component="input"
                    type="checkbox"
                    checked={selected.has(p.language) && approvable(p)}
                    disabled={!approvable(p)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => toggle([p.language], e.target.checked)}
                    sx={{ m: 0, mt: 0.25, accentColor: '#7c3aed', flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ fontSize: 13 }}>
                      {p.text !== undefined && marked(p.text, p.survived)}
                      {missing.map((_, i) => (
                        <Box
                          key={i}
                          component="span"
                          title={t('promptStudio.trans.nounMissing')}
                          sx={{ display: 'inline-block', width: 52, height: 14, ml: 0.5, verticalAlign: 'middle', border: '1.5px dashed #f59e0b', borderRadius: 0.5 }}
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 0.25, fontSize: 11.5, color: 'text.disabled' }}>
                      {p.flags.map((f, i) => (
                        <Box key={i} component="span" sx={{ fontSize: 11, color: '#b45309', bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 1, px: 0.75 }}>
                          {t(FLAG_KEY[f.kind])}
                        </Box>
                      ))}
                      {anyway && <span>{t('promptStudio.trans.approveAnyway')}</span>}
                      {!flagged && p.entitiesChecked.length > 0 && (
                        <span>{t('promptStudio.trans.nounKept', { entity: p.entitiesChecked.join(', ') })}</span>
                      )}
                      {p.entitiesChecked.length === 0 && <span>{t('promptStudio.trans.noTrackedNoun')}</span>}
                    </Box>
                    {err && (
                      <Box sx={{ fontSize: 11.5, color: '#b45309', mt: 0.25 }}>
                        {err === 'BRAND_PROMPT_LIMIT' || err === 'AXIS_PROMPT_LIMIT'
                          ? slotText(t, rowErrorSlot(err, axis))
                          : `${name} · ${t('promptStudio.common.saveFailed')}`}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      })}

      {/* Foot */}
      {foot.refusal && <LimitReasonLine reason={foot.refusal.reason} left={foot.refusal.left} />}
      {foot.previewFailed && <Box sx={{ fontSize: 11.5, color: '#b45309', mt: 0.5 }}>{t('promptStudio.fit.previewFailed')}</Box>}
      {saveFailed && <Box sx={{ fontSize: 11.5, color: '#b45309', mt: 0.5 }}>{t('promptStudio.common.saveFailed')}</Box>}
      {firstRun.map((l) => (
        <Box key={`${l.axis}-${l.key}`} sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.5 }}>
          {t(l.key, { count: l.count, axis: t(`promptStudio.axis.${l.axis}`), date: l.date })}
        </Box>
      ))}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
        <Button variant="text" size="small" onClick={onBack} disabled={saving}>
          {t('common.back')}
        </Button>
        <Tooltip title={foot.refusal?.reason ? slotText(t, foot.refusal.reason) : foot.previewFailed ? t('promptStudio.fit.previewFailed') : ''} arrow>
          <Box component="span">
            <Button variant="contained" size="small" onClick={onApprove} disabled={foot.disabled} sx={{ fontWeight: 600 }}>
              {t('promptStudio.trans.approve', { ok: items.length, all: list.length })}
            </Button>
          </Box>
        </Tooltip>
      </Box>

      <QuotaWarningDialog
        open={quotaOpen}
        warnings={preview.res?.warnings ?? null}
        onConfirm={() => void save()}
        onCancel={() => setQuotaOpen(false)}
      />
    </Box>
  );
}
